import React, { useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import SEO from './SEO';
import seoData from '../seoData';
import { 
  ArrowRight, 
  CheckCircle, 
  Shield, 
  Award, 
  Wrench,
  Factory,
  Droplets,
  Flame,
  Gauge,
  Clock,
  MapPin,
  Phone
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Belzona = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const belzonaProducts = [
    {
      category: "Metal Repair",
      description: "Rebuild and protect metal equipment without hot work",
      products: ["Belzona 1111", "Belzona 1212", "Belzona 1311"],
      icon: Wrench,
      color: "blue"
    },
    {
      category: "Concrete Repair",
      description: "Restore and protect concrete surfaces and structures",
      products: ["Belzona 4111", "Belzona 4131", "Belzona 4154"],
      icon: Factory,
      color: "gray"
    },
    {
      category: "Fluid Handling",
      description: "Protect pumps, valves, and pipelines from erosion and corrosion",
      products: ["Belzona 1341", "Belzona 1391", "Belzona 1831"],
      icon: Droplets,
      color: "cyan"
    },
    {
      category: "High Temperature",
      description: "Solutions for equipment operating at elevated temperatures",
      products: ["Belzona 1511", "Belzona 1521", "Belzona 1593"],
      icon: Flame,
      color: "orange"
    },
    {
      category: "Protective Coatings",
      description: "Long-lasting protection against corrosion and chemical attack",
      products: ["Belzona 5811", "Belzona 5831", "Belzona 5891"],
      icon: Shield,
      color: "green"
    },
    {
      category: "Elastomers",
      description: "Flexible solutions for rubber and elastomer repair",
      products: ["Belzona 2111", "Belzona 2131", "Belzona 2141"],
      icon: Gauge,
      color: "purple"
    }
  ];

  const benefits = [
    {
      title: "No Hot Work Required",
      description: "Eliminate fire permits, hot work, and associated downtime with cold-applied solutions"
    },
    {
      title: "In-Situ Repairs",
      description: "Repair equipment on-site without costly removal or replacement"
    },
    {
      title: "Minimal Downtime",
      description: "Fast-curing formulations get you back in operation quickly"
    },
    {
      title: "Long-Term Protection",
      description: "Proven performance with warranties backing our installations"
    },
    {
      title: "Cost-Effective",
      description: "Fraction of the cost compared to equipment replacement"
    },
    {
      title: "Certified Application",
      description: "BCS technicians are Belzona-certified for quality installations"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: "bg-blue-100 text-blue-700",
      gray: "bg-gray-100 text-gray-700",
      cyan: "bg-cyan-100 text-cyan-700",
      orange: "bg-orange-100 text-orange-700",
      green: "bg-green-100 text-green-700",
      purple: "bg-purple-100 text-purple-700"
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData.belzona?.title} description={seoData.belzona?.description} path="/belzona" keywords={seoData.belzona?.keywords || []} />
      
      <PageHero
        title="Belzona Solutions"
        description="BCS is the preferred vendor of Belzona California Inc., providing expert application of industry-leading polymer repair and protection systems."
        backgroundKey="belzona"
        showButton={false}
      />

      {/* Partnership Banner */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Award className="text-amber-400" size={48} />
              <div>
                <h3 className="text-xl font-bold text-white">Preferred Vendor of Belzona California Inc.</h3>
                <p className="text-blue-200">Certified applicators with decades of experience</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                <MapPin className="text-green-400" size={18} />
                <span className="text-white">Serving All of California</span>
              </div>
              <Button 
                className="bg-green-500 hover:bg-green-600"
                onClick={() => window.location.href = 'tel:+19493719948'}
              >
                <Phone className="mr-2" size={18} />
                (949) 371-9948
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Belzona */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-900">About Belzona</Badge>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                World-Leading Polymer Technology
              </h2>
              <p className="text-gray-600 mb-6">
                Belzona is a global leader in the development and manufacture of polymer repair composites 
                and industrial protective coatings. For over 60 years, Belzona products have been trusted 
                by industries worldwide to solve complex maintenance challenges.
              </p>
              <p className="text-gray-600 mb-6">
                As the preferred vendor of Belzona California Inc., BCS brings expert knowledge and 
                certified application techniques to every project. Our technicians undergo rigorous 
                training to ensure the highest quality installations.
              </p>
              <div className="space-y-3">
                {[
                  "Cold-applied solutions - no hot work permits required",
                  "In-situ repairs without equipment removal",
                  "Proven track record across all industries",
                  "Comprehensive warranty programs"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8">
              <img 
                src="https://images.pexels.com/photos/29224591/pexels-photo-29224591.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=900&w=1600" 
                alt="Industrial repair application"
                className="rounded-xl w-full h-64 object-cover mb-6"
              />
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-blue-900">60+</div>
                  <div className="text-gray-600 text-sm">Years of Innovation</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-blue-900">120+</div>
                  <div className="text-gray-600 text-sm">Countries Worldwide</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-900">Product Solutions</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Belzona Product Categories
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions for every industrial repair and protection challenge
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {belzonaProducts.map((product, index) => {
              const IconComponent = product.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg ${getColorClasses(product.color)} flex items-center justify-center mb-4`}>
                      <IconComponent size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{product.category}</h3>
                    <p className="text-gray-600 mb-4">{product.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {product.products.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-900">Why Belzona</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Benefits of Belzona Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4 p-6 bg-slate-50 rounded-xl">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Common Applications</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">
              Belzona products solve a wide range of industrial maintenance challenges
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Pump Rebuilding",
              "Pipe Repair",
              "Tank Linings",
              "Valve Repair",
              "Heat Exchanger Protection",
              "Concrete Restoration",
              "Floor Coatings",
              "Roof Waterproofing",
              "Chiller Tube Repair",
              "Erosion Protection",
              "Chemical Resistance",
              "Emergency Repairs"
            ].map((app, index) => (
              <div key={index} className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3">
                <CheckCircle className="text-green-400 flex-shrink-0" size={16} />
                <span className="text-sm">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <Clock className="mx-auto mb-4 text-blue-900" size={48} />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Need a Belzona Solution?
          </h2>
          <p className="text-gray-600 mb-8">
            Contact BCS today to discuss your repair or protection project. Our certified technicians 
            will recommend the right Belzona solution for your application.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-blue-900 hover:bg-blue-800"
              onClick={() => navigate('/#contact')}
            >
              Request a Quote <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.location.href = 'tel:+19493719948'}
            >
              <Phone className="mr-2" size={20} />
              Call (949) 371-9948
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Belzona;
